import { Card, Icon, NonIdealState, Spinner } from "@blueprintjs/core";
import classNames from "classnames";
import React, { useEffect, useState } from "react";

import { Button } from "../../../../components";
import { useAppSelector } from "../../../../redux/hooks";
import { Sport } from "../../../../redux/slices";
import { GetPlaybookPlayCount } from "../../../../utils";
import { usePlaybookContext } from "../../playbook-context";
import DraggablePlay from "../draggable-play/draggable-play";
import { PlayDisplay } from "../play-display/play-display";
import PlayListItem from "../play-list-item/play-list-item";
import styles from "./playbook-card.module.scss";

const PDFViewer = ({ pdfUrl, isLoading }: { pdfUrl: string; isLoading: boolean }) => {
  return (
    <div data-testid="pdf-viewer" style={{ height: "95%" }}>
      {isLoading ? (
        <Spinner />
      ) : (
        <object type="application/pdf" data={pdfUrl} width="100%" height="100%" title="PDF Viewer" />
      )}
    </div>
  );
};

export const PlaybookCard = () => {
  const { name: sport } = useAppSelector((state) => state.sport);
  const isBasketball = sport === Sport.BASKETBALL;
  const [displayMode, setDisplayMode] = useState<"list" | "grid" | "pdf">(isBasketball ? "pdf" : "list");

  const {
    setAddingPlay,
    addingPlay,
    playsToAdd,
    addPlaysToPlaybook,
    localPlaybook: playbook,
    renderingPDF,
    playbookPDFUrl,
  } = usePlaybookContext();

  const isClassicPlaybook = playbook?.origin === "CLASSIC";

  useEffect(() => {
    if (playbook && !isClassicPlaybook && GetPlaybookPlayCount(playbook) === 0) {
      setDisplayMode("list");
    }
  }, [playbook, isClassicPlaybook]);

  if (!playbook) return null;

  const handlePDFDisplayMode = () => {
    setDisplayMode("pdf");
  };

  const handleAddPlaysToPlaybook = () => {
    addPlaysToPlaybook(playsToAdd);
    setAddingPlay(false);
  };

  const PlayComponent = displayMode === "list" ? PlayListItem : PlayDisplay;

  const allowPDFDownload = GetPlaybookPlayCount(playbook) > 0 && isBasketball; // temporary until we have pdf generation support for other sports
  const showPDFViewer = isClassicPlaybook || displayMode === "pdf";
  // The PDF viewer has its own empty state.
  const showEmptyState = GetPlaybookPlayCount(playbook) === 0 && !showPDFViewer;

  return (
    <Card className={styles.playbookCard}>
      <div className={styles.playbookHeader}>
        <div className={styles.playbookName}>{playbook.title}</div>
      </div>
      {!isClassicPlaybook && (
        <>
          <div
            className={styles.playbookSubHeader}
            style={{ justifyContent: displayMode === "pdf" ? "flex-end" : "space-between" }}
          >
            {displayMode !== "pdf" && (
              <div className={styles.playbookManagementOptions}>
                <Button
                  className={classNames("addPlaysButton", {
                    [styles.noPlaysToAdd || ""]: !addingPlay || playsToAdd.length === 0,
                  })}
                  icon="plus"
                  onClick={handleAddPlaysToPlaybook}
                >
                  Add {playsToAdd.length} Plays
                </Button>
                {addingPlay ? (
                  <Button icon="cross" onClick={() => setAddingPlay(false)}>
                    Cancel
                  </Button>
                ) : (
                  <Button icon="add" onClick={() => setAddingPlay(true)}>
                    Add Play
                  </Button>
                )}
              </div>
            )}
            <div className={styles.playbookDisplayOptions}>
              <Icon
                id="list-display-button"
                icon="list"
                className={classNames(`${styles.icon}`, {
                  [styles.displayModeSelected || ""]: displayMode === "list",
                  [styles.displayModeUnselected || ""]: displayMode !== "list",
                })}
                onClick={() => setDisplayMode("list")}
              />

              <Icon
                icon="layout-grid"
                id="grid-display-button"
                className={classNames(`${styles.icon}`, {
                  [styles.displayModeSelected || ""]: displayMode === "grid",
                  [styles.displayModeUnselected || ""]: displayMode !== "grid",
                })}
                onClick={() => setDisplayMode("grid")}
              />

              {allowPDFDownload && (
                <i
                  id="pdf-display-button"
                  className="fa-regular fa-file-pdf"
                  onClick={() => handlePDFDisplayMode()}
                  style={{ color: displayMode === "pdf" ? "var(--team-color)" : "#bababa", cursor: "pointer" }}
                ></i>
              )}
            </div>
          </div>

          {displayMode !== "pdf" && (
            <div
              className={classNames({
                [styles.playbookListContainer || ""]: displayMode === "list",
                [styles.playbookDisplayContainer || ""]: displayMode === "grid",
              })}
            >
              {playbook.chapters.map((chapter) => {
                return chapter.sections.map((section) => {
                  return section.plays.map((playId, index) => {
                    return (
                      <DraggablePlay key={playId} playId={playId} index={index}>
                        <PlayComponent playId={playId} />
                      </DraggablePlay>
                    );
                  });
                });
              })}
            </div>
          )}
        </>
      )}

      {showPDFViewer && <PDFViewer pdfUrl={playbookPDFUrl || ""} isLoading={renderingPDF} />}
      {showEmptyState && (
        <div className={styles.emptyStateContainer}>
          <NonIdealState
            icon="document"
            title="No Plays in this Playbook"
            description="Click Add Play to get started."
            layout="vertical"
          />
        </div>
      )}
    </Card>
  );
};
